import { type ReactNode, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

import { OAUTH_CHANNEL_NAME } from './oauth-channel.const'
import type { OAuthMessageData } from './oauth-message.interface'

const CLOSE_DELAY_MS = 1500

const buildOAuthMessage = (searchParams: URLSearchParams): OAuthMessageData => {
  const code = searchParams.get('code')
  const state = searchParams.get('state')

  if (code && state) {
    return { type: 'oauth-callback', code, state }
  }

  const error = searchParams.get('error')
  return { type: 'oauth-error', error: error ?? 'cancelled' }
}

/**
 * Minimal page rendered in the OAuth popup after provider redirect.
 * Extracts code/state from URL and sends them to the parent window via BroadcastChannel.
 * Uses BroadcastChannel instead of window.opener.postMessage because browsers
 * nullify window.opener after cross-origin navigation during OAuth flow.
 */
export const OAuthCallbackPage: React.FC = (): ReactNode => {
  const [searchParams] = useSearchParams()
  const isSentRef = useRef(false)

  useEffect(() => {
    if (isSentRef.current) return
    isSentRef.current = true

    const channel = new BroadcastChannel(OAUTH_CHANNEL_NAME)
    channel.postMessage(buildOAuthMessage(searchParams))
    channel.close()

    const timer = setTimeout(() => {
      window.close()
    }, CLOSE_DELAY_MS)

    return () => clearTimeout(timer)
  }, [searchParams])

  return (
    <div className="oauth-callback-page flex h-screen items-center justify-center">
      <p className="text-muted-foreground text-sm">Connecting... You can close this window.</p>
    </div>
  )
}

OAuthCallbackPage.displayName = 'OAuthCallbackPage'
